if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
      // S2G thumbnail pager is handled by assets/s2g-product-gallery-fix.js
    }

    initS2GThumbnailPager() {
      const thumbnails = this.elements.thumbnails;
      if (!thumbnails) return;

      const items = Array.from(thumbnails.querySelectorAll('[data-target]'));
      const prevButton = thumbnails.querySelector('.slider-button--prev');
      const nextButton = thumbnails.querySelector('.slider-button--next');
      const maxVisible = 4;
      let startIndex = 0;

      thumbnails.classList.add('s2g-thumbnail-pager');

      if (!prevButton || !nextButton || items.length <= maxVisible) {
        thumbnails.classList.add('s2g-thumbnail-pager--no-arrows');
        if (prevButton) prevButton.hidden = true;
        if (nextButton) nextButton.hidden = true;
        items.forEach((item) => item.classList.remove('s2g-thumb-hidden'));
        return;
      }

      thumbnails.classList.add('s2g-thumbnail-pager--has-arrows');

      const update = () => {
        startIndex = Math.max(0, Math.min(startIndex, items.length - maxVisible));

        items.forEach((item, index) => {
          item.classList.toggle('s2g-thumb-hidden', index < startIndex || index >= startIndex + maxVisible);
        });

        const atStart = startIndex <= 0;
        const atEnd = startIndex >= items.length - maxVisible;

        // Shopify's native slider can mark these buttons as disabled, which stops our
        // custom thumbnail pager from receiving clicks. We control visibility instead.
        [prevButton, nextButton].forEach((button) => {
          button.disabled = false;
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
          button.tabIndex = 0;
        });

        prevButton.hidden = atStart;
        nextButton.hidden = atEnd;
      };

      const showIndex = (index) => {
        if (index < startIndex) startIndex = index;
        if (index >= startIndex + maxVisible) startIndex = index - maxVisible + 1;
        startIndex = Math.max(0, Math.min(startIndex, items.length - maxVisible));
        update();
      };

      thumbnails.s2gShowThumbnailIndex = showIndex;

      const goPrev = (event) => {
        if (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
        }
        startIndex = Math.max(0, startIndex - 1);
        update();
      };

      const goNext = (event) => {
        if (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
        }
        startIndex = Math.min(items.length - maxVisible, startIndex + 1);
        update();
      };

      prevButton.addEventListener('click', goPrev, true);
      nextButton.addEventListener('click', goNext, true);
      prevButton.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') goPrev(event); }, true);
      nextButton.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') goNext(event); }, true);

      const activeIndex = items.findIndex((item) => item.querySelector('button[aria-current]'));
      if (activeIndex > -1) showIndex(activeIndex);
      else update();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (typeof this.elements.thumbnails.s2gShowThumbnailIndex === 'function') {
        const allThumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeIndex = allThumbnails.indexOf(thumbnail);
        if (activeIndex > -1) this.elements.thumbnails.s2gShowThumbnailIndex(activeIndex);
        return;
      }

      if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
      if (this.elements.thumbnails.slider) this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
